/**
 * TemplateRunnerDialog — parametrize → run → view → export flow for a
 * single template. Event picker uses AuthContext's `events` list (same
 * shape as EventSwitcher). Date picker is shown only when the template
 * declares a `date` required_param (today only `daily-summary`).
 *
 * Export buttons:
 *   - JSON: downloads the raw section payload via a blob URL.
 *   - CSV:  re-runs the template with ?format=csv and downloads the file.
 *
 * Print layout: wrapper includes `print:block` and disables the modal
 * chrome under `@media print` via Tailwind's `print:` variant.
 */

import { useState, useMemo } from 'react'
import { X, Download, FileText, Loader2 } from 'lucide-react'

import type { EventSummary } from '@/contexts/AuthContext'
import type { TemplateSummary, TemplateRunResult, TemplateSection } from './types'
import { humanLabel, autoFormat } from '@/lib/formatters'

interface Props {
  template: TemplateSummary
  events: EventSummary[]
  defaultEventId: number | null
  onClose: () => void
}

export function TemplateRunnerDialog({ template, events, defaultEventId, onClose }: Props) {
  const [eventId, setEventId] = useState<string>(
    defaultEventId ? String(defaultEventId) : '',
  )
  const [compareToEventId, setCompareToEventId] = useState<string>('')
  const [date, setDate] = useState<string>('')
  const [allowRunning, setAllowRunning] = useState<boolean>(false)
  const [running, setRunning] = useState<boolean>(false)
  const [result, setResult] = useState<TemplateRunResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const needsDate = template.required_params.includes('date')
  const allowsRunningFlag = template.optional_params.includes('allow_running')
  const allowsComparator = template.optional_params.includes('compare_to_event_id')

  const canRun = useMemo(() => {
    if (!eventId) return false
    if (needsDate && !date) return false
    return true
  }, [eventId, date, needsDate])

  function buildParams(): Record<string, unknown> {
    const params: Record<string, unknown> = { event_id: Number(eventId) }
    if (needsDate) params.date = date
    if (allowsRunningFlag && allowRunning) params.allow_running = true
    if (allowsComparator && compareToEventId) {
      params.compare_to_event_id = Number(compareToEventId)
    }
    return params
  }

  async function run() {
    setRunning(true)
    setError(null)
    setResult(null)
    try {
      const res = await fetch(`/api/reports/templates/${template.id}/run`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ params: buildParams() }),
      })
      const body = await res.json()
      if (!res.ok) {
        setError(body?.message || body?.error || `HTTP ${res.status}`)
        return
      }
      setResult(body as TemplateRunResult)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setRunning(false)
    }
  }

  function downloadJson() {
    if (!result) return
    const blob = new Blob([JSON.stringify(result, null, 2)], {
      type: 'application/json',
    })
    triggerDownload(blob, `${template.id}-${Date.now()}.json`)
  }

  async function downloadCsv() {
    try {
      const res = await fetch(`/api/reports/templates/${template.id}/run?format=csv`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ params: buildParams() }),
      })
      if (!res.ok) {
        setError(`CSV export failed: HTTP ${res.status}`)
        return
      }
      const blob = await res.blob()
      triggerDownload(blob, `${template.id}-${Date.now()}.csv`)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 print:static print:bg-transparent print:p-0"
      data-testid="template-runner-dialog"
      role="dialog"
      aria-modal="true"
      aria-label={template.title}
    >
      <div className="flex w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-slate-800 print:max-w-none print:shadow-none">
        <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-700 print:border-none">
          <div className="min-w-0">
            <h2 className="font-display text-lg font-semibold text-butler-800 dark:text-butler-100">
              {template.title}
            </h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {template.description}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-700 print:hidden"
            aria-label="Close"
            data-testid="template-dialog-close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex flex-col gap-4 px-5 py-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="text-xs font-medium text-slate-700 dark:text-slate-200">
              Event
              <select
                value={eventId}
                onChange={(e) => setEventId(e.target.value)}
                className="mt-1 block w-full rounded-md border border-slate-200 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                data-testid="template-param-event"
              >
                <option value="">Select an event…</option>
                {events.map((e) => (
                  <option key={e.EventID} value={String(e.EventID)}>
                    {e.EventName}
                  </option>
                ))}
              </select>
            </label>

            {needsDate && (
              <label className="text-xs font-medium text-slate-700 dark:text-slate-200">
                Date
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-slate-200 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                  data-testid="template-param-date"
                />
              </label>
            )}

            {allowsComparator && (
              <label className="text-xs font-medium text-slate-700 dark:text-slate-200">
                Compare to (optional — prior-year event)
                <select
                  value={compareToEventId}
                  onChange={(e) => setCompareToEventId(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-slate-200 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                  data-testid="template-param-compare-to"
                >
                  <option value="">No comparison</option>
                  {events
                    .filter((e) => String(e.EventID) !== eventId)
                    .map((e) => (
                      <option key={e.EventID} value={String(e.EventID)}>
                        {e.EventName}
                      </option>
                    ))}
                </select>
              </label>
            )}
          </div>

          {allowsRunningFlag && (
            <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
              <input
                type="checkbox"
                checked={allowRunning}
                onChange={(e) => setAllowRunning(e.target.checked)}
                data-testid="template-param-allow-running"
              />
              Run against event that hasn&apos;t ended yet
            </label>
          )}

          <div className="flex items-center gap-2 print:hidden">
            <button
              type="button"
              disabled={!canRun || running}
              onClick={run}
              className="inline-flex items-center gap-1.5 rounded-md bg-butler-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-butler-700 disabled:opacity-50"
              data-testid="template-run-button"
            >
              {running ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden={true} />
                  Running…
                </>
              ) : (
                'Run'
              )}
            </button>

            {result && (
              <>
                <button
                  type="button"
                  onClick={downloadJson}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  data-testid="template-export-json"
                >
                  <Download className="h-3.5 w-3.5" aria-hidden={true} />
                  JSON
                </button>
                <button
                  type="button"
                  onClick={downloadCsv}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  data-testid="template-export-csv"
                >
                  <FileText className="h-3.5 w-3.5" aria-hidden={true} />
                  CSV
                </button>
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  data-testid="template-print"
                >
                  Print
                </button>
              </>
            )}
          </div>

          {error && (
            <div
              className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-200"
              data-testid="template-run-error"
            >
              {error}
            </div>
          )}
        </div>

        <div className="max-h-[60vh] overflow-y-auto border-t border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-700 dark:bg-slate-900 print:max-h-none print:overflow-visible print:bg-white">
          {result ? (
            <>
              {result.pii_policy !== 'none' && (
                <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
                  Band IDs are hashed for privacy. No names, emails, or phone numbers are included.
                </div>
              )}
              {result.error ? (
                <div
                  className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200"
                  data-testid="template-gate-error"
                >
                  {result.message || result.error}
                </div>
              ) : (
                <div
                  className="flex flex-col gap-4"
                  data-testid="template-run-result"
                >
                  {result.sections.map((s) => (
                    <SectionBlock key={s.key} section={s} />
                  ))}
                </div>
              )}
            </>
          ) : (
            <p
              className="text-xs text-slate-500 dark:text-slate-400"
              data-testid="template-empty-state"
            >
              Pick an event, then press Run to generate the report.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

function SectionBlock({ section }: { section: TemplateSection }) {
  return (
    <div
      className="rounded-md border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800"
      data-testid={`template-section-${section.key}`}
    >
      <h3 className="text-xs font-semibold uppercase tracking-wider text-butler-600 dark:text-butler-300">
        {section.label}
      </h3>
      {section.error ? (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-200">
          {section.error}
        </p>
      ) : (
        <SectionBody data={section.data} />
      )}
    </div>
  )
}

function SectionBody({ data }: { data: unknown }) {
  if (data == null) {
    return <p className="mt-2 text-xs text-ink-soft">(no data)</p>
  }
  if (Array.isArray(data)) {
    if (data.length === 0) {
      return <p className="mt-2 text-xs text-ink-soft">(no rows)</p>
    }
    const rows = data as Array<Record<string, unknown>>
    const headers = Object.keys(rows[0])
    const isNumeric = (key: string) =>
      key.endsWith('_cents') || key === 'revenue' || key === 'orders' ||
      key === 'scans' || key === 'guests' || key.endsWith('_total') ||
      key === 'pct_of_orders' || key === 'repeat_play_pct'
    return (
      <div className="mt-2 overflow-x-auto">
        <table className="min-w-full text-left text-[13px]">
          <thead>
            <tr className="border-b border-hairline">
              {headers.map((h) => (
                <th
                  key={h}
                  className={`px-3 py-2 text-[11px] font-medium uppercase tracking-[0.5px] text-ink-soft ${
                    isNumeric(h) ? 'text-right' : ''
                  }`}
                >
                  {humanLabel(h)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={i}
                className={`border-t border-hairline transition-colors hover:bg-surface-hover ${
                  i % 2 === 1 ? 'bg-surface-stripe' : ''
                }`}
              >
                {headers.map((h) => (
                  <td
                    key={h}
                    className={`px-3 py-2 tabular-nums text-ink ${
                      isNumeric(h) ? 'text-right' : ''
                    }`}
                  >
                    {autoFormat(h, row[h])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }
  if (typeof data === 'object') {
    const obj = data as Record<string, unknown>
    const entries = Object.entries(obj)
    return (
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
        {entries.map(([key, val]) => (
          <div key={key} className="contents">
            <dt className="text-[11px] font-medium uppercase tracking-[0.5px] text-ink-soft py-1">
              {humanLabel(key)}
            </dt>
            <dd className="text-sm tabular-nums text-ink py-1">
              {autoFormat(key, val)}
            </dd>
          </div>
        ))}
      </dl>
    )
  }
  return <p className="mt-2 text-sm text-ink">{String(data)}</p>
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
